// RigSentinel — Step-Down Chart (inline SVG).
//
// QA Fix Pack 2 · Issue 4 — The Driller's Step-Down Schedule is a
// mandatory commercial-release visual. The engine already produces the
// full row set (`outputs.schedule.rows[]`); this component simply
// renders that same dataset as a pressure-vs-strokes line graph.
//
// Rules of engagement:
//   * Zero new dependencies — pure SVG.
//   * Zero engineering math — uses the row values verbatim.
//   * Same data source as `ScheduleTable`.
//   * Print-safe (black outline, monochrome-legible fills, no gradients).
//   * When the schedule is not OK, renders an explicit "no data" note —
//     never a blank frame.

import React from "react";

const _fmtInt = (v) => (v == null ? "—" : Math.round(v).toLocaleString());

export function ScheduleChart({ schedule, maaspInitialPsi, testIdPrefix = "ks-schedule-chart" }) {
  if (!schedule || schedule.ok !== true || !Array.isArray(schedule.rows) || schedule.rows.length === 0) {
    return (
      <div data-testid={`${testIdPrefix}-empty`}
           className="rounded-md border border-amber-500/30 p-3 text-[11px] text-amber-200/90">
        No chart available — the Step-Down Schedule has no computed rows yet.
      </div>
    );
  }

  const rows = schedule.rows;
  const maxStroke = Math.max(...rows.map((r) => Number(r.stroke) || 0), 1);
  const maxPress  = Math.max(...rows.map((r) => Number(r.pressurePsi) || 0), Number(maaspInitialPsi) || 0, 1);
  const yMax = Math.ceil(maxPress / 100) * 100;

  const W = 720, H = 260;
  const PAD_L = 56, PAD_R = 16, PAD_T = 16, PAD_B = 42;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const x = (s) => PAD_L + (Number(s) / maxStroke) * plotW;
  const y = (p) => PAD_T + plotH - (Number(p) / yMax) * plotH;

  // Split rows on phase change so each segment renders distinctly.
  // Surface → Bit → Shoe → Surface (P0 display correction) — the graph
  // used to stop at "kill" (strokes to bit); "bit-to-shoe" and
  // "shoe-to-surface" are the SAME already-computed rows extended past the
  // bit, held at the constant FCP the "kill" ramp already ends on.
  const circulate     = rows.filter((r) => r.phase === "circulate");
  const kill          = rows.filter((r) => r.phase === "kill");
  const bitToShoe     = rows.filter((r) => r.phase === "bit-to-shoe");
  const shoeToSurface = rows.filter((r) => r.phase === "shoe-to-surface");

  const path = (pts) => pts.map((r, i) =>
    `${i === 0 ? "M" : "L"}${x(r.stroke).toFixed(1)},${y(r.pressurePsi).toFixed(1)}`
  ).join(" ");

  // Prepend the previous phase's last point so each new segment's line is
  // visually continuous with the one before it (no invented data point —
  // it's the exact same row the prior segment already ended on).
  const bitToShoePath = bitToShoe.length > 0
    ? [...(kill.length ? [kill[kill.length - 1]] : []), ...bitToShoe]
    : [];
  const shoeToSurfacePath = shoeToSurface.length > 0
    ? [...(bitToShoePath.length ? [bitToShoePath[bitToShoePath.length - 1]]
        : (kill.length ? [kill[kill.length - 1]] : [])), ...shoeToSurface]
    : [];

  // Y-axis grid ticks — 4 divisions, always integer psi labels.
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(t * yMax));
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(t * maxStroke));

  const maasp = Number(maaspInitialPsi);
  const showMaasp = Number.isFinite(maasp) && maasp > 0 && maasp <= yMax;

  return (
    <div data-testid={testIdPrefix} className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img"
           aria-label="Driller's Step-Down schedule: standpipe pressure vs strokes"
           style={{ minWidth: W, display: "block", background: "transparent" }}>
        <rect x={PAD_L} y={PAD_T} width={plotW} height={plotH}
              fill="rgba(255,255,255,0.02)" stroke="currentColor" strokeOpacity="0.35" />

        {/* Y-axis grid + labels */}
        {yTicks.map((t, i) => (
          <g key={`y${i}`}>
            <line x1={PAD_L} x2={PAD_L + plotW} y1={y(t)} y2={y(t)}
                  stroke="currentColor" strokeOpacity="0.12" strokeDasharray="2 3" />
            <text x={PAD_L - 6} y={y(t) + 3} textAnchor="end"
                  fontSize="10" fill="currentColor" opacity="0.7" fontFamily="ui-monospace, monospace">
              {t.toLocaleString()}
            </text>
          </g>
        ))}
        {/* X-axis grid + labels */}
        {xTicks.map((t, i) => (
          <g key={`x${i}`}>
            <line x1={x(t)} x2={x(t)} y1={PAD_T} y2={PAD_T + plotH}
                  stroke="currentColor" strokeOpacity="0.10" strokeDasharray="2 3" />
            <text x={x(t)} y={H - 24} textAnchor="middle"
                  fontSize="10" fill="currentColor" opacity="0.7" fontFamily="ui-monospace, monospace">
              {t.toLocaleString()}
            </text>
          </g>
        ))}
        <text x={PAD_L + plotW / 2} y={H - 6} textAnchor="middle"
              fontSize="10.5" fill="currentColor" opacity="0.85">Strokes</text>
        <text x={12} y={PAD_T + plotH / 2}
              transform={`rotate(-90 12 ${PAD_T + plotH / 2})`}
              textAnchor="middle" fontSize="10.5" fill="currentColor" opacity="0.85">
          Standpipe pressure (psi)
        </text>

        {/* MAASP reference — dashed red line */}
        {showMaasp && (
          <g data-testid={`${testIdPrefix}-maasp`}>
            <line x1={PAD_L} x2={PAD_L + plotW} y1={y(maasp)} y2={y(maasp)}
                  stroke="#dc2626" strokeWidth="1.25" strokeDasharray="4 3" />
            <text x={PAD_L + plotW - 4} y={y(maasp) - 4} textAnchor="end"
                  fontSize="9.5" fill="#dc2626" fontFamily="ui-monospace, monospace">
              MAASP {_fmtInt(maasp)} psi
            </text>
          </g>
        )}

        {/* Circulate phase (Driller's Method Phase 1 only) — sky, matching
            ScheduleTable's circulate badge colour (ticket 237121, item 4).
            This is always exactly ONE anchor row (hold standpipe at ICP
            while circulating the kick out) — the schedule generator does
            not yet compute intermediate circulation strokes for this phase
            (see drillersSchedule.js), so there is deliberately no line to
            draw here: a single point has nothing to connect to. Rendering
            a ramp would fabricate data that doesn't exist. */}
        {circulate.length > 0 && (
          <path d={path(circulate)} fill="none" stroke="#38bdf8" strokeWidth="2"
                data-testid={`${testIdPrefix}-circulate`} />
        )}
        {/* Kill phase — the real, calculated ICP -> FCP ramp as kill mud is
            displaced down the drill string. Amber, matching ScheduleTable's
            kill badge colour. */}
        {kill.length > 0 && (
          <path d={path(kill)} fill="none" stroke="#f59e0b" strokeWidth="2"
                data-testid={`${testIdPrefix}-kill`} />
        )}
        {/* Surface → Bit → Shoe → Surface (P0 display correction) — FCP is
            reached the instant kill mud is at the bit (end of the "kill"
            line above); it then HOLDS constant while that same kill mud
            continues bit→shoe→surface. Same amber line, continuing —
            deliberately not a different colour/ramp: there is no further
            pressure change to distinguish, only continued circulation. */}
        {bitToShoePath.length > 0 && (
          <path d={path(bitToShoePath)} fill="none" stroke="#f59e0b" strokeWidth="2"
                data-testid={`${testIdPrefix}-bit-to-shoe`} />
        )}
        {shoeToSurfacePath.length > 0 && (
          <path d={path(shoeToSurfacePath)} fill="none" stroke="#f59e0b" strokeWidth="2"
                data-testid={`${testIdPrefix}-shoe-to-surface`} />
        )}
        {/* Row markers — small dots on each entry, coloured to match phase */}
        {rows.map((r, i) => (
          <circle key={i} cx={x(r.stroke)} cy={y(r.pressurePsi)} r="2.5"
                  fill={r.phase === "circulate" ? "#38bdf8" : "#f59e0b"} />
        ))}

        {/* ICP / FCP / shoe / surface milestone annotations (P0 display
            correction) — the first row IS the ICP, the last "kill" row IS
            the FCP (= kill mud reaching the bit), the last "bit-to-shoe"
            row IS kill mud reaching the shoe, and the last
            "shoe-to-surface" row IS kill mud reaching surface. Labels
            only — no data added, moved, or recomputed. */}
        {(() => {
          const first = rows[0];
          const icpX = x(first.stroke), icpY = y(first.pressurePsi);
          const icpLabelY = Math.max(PAD_T + 10, icpY - 18);
          const hasFcp = kill.length > 1;
          const bitRow = hasFcp ? kill[kill.length - 1] : null;
          const shoeRow = bitToShoe.length > 0 ? bitToShoe[bitToShoe.length - 1] : null;
          const surfaceRow = shoeToSurface.length > 0 ? shoeToSurface[shoeToSurface.length - 1] : null;
          return (
            <g fontFamily="ui-monospace, monospace">
              <circle cx={icpX} cy={icpY} r="5" fill="none" stroke="#10b981" strokeWidth="2"
                      data-testid={`${testIdPrefix}-icp-point`} />
              <text x={icpX + 8} y={icpLabelY} fontSize="9" fontWeight="700" fill="#10b981"
                    data-testid={`${testIdPrefix}-icp-label`}>
                <tspan x={icpX + 8} dy="0">Initial Circulating Pressure (ICP)</tspan>
                <tspan x={icpX + 8} dy="11" fontSize="10.5">{_fmtInt(first.pressurePsi)} psi</tspan>
              </text>
              {bitRow && (() => {
                const fcpX = x(bitRow.stroke), fcpY = y(bitRow.pressurePsi);
                const fcpLabelY = Math.max(PAD_T + 10, fcpY - 26);
                return (
                  <>
                    <circle cx={fcpX} cy={fcpY} r="5" fill="none" stroke="#f43f5e" strokeWidth="2"
                            data-testid={`${testIdPrefix}-fcp-point`} />
                    <text x={fcpX - 8} y={fcpLabelY} textAnchor="end" fontSize="9" fontWeight="700" fill="#f43f5e"
                          data-testid={`${testIdPrefix}-fcp-label`}>
                      <tspan x={fcpX - 8} dy="0">Final Circulating Pressure (FCP)</tspan>
                      <tspan x={fcpX - 8} dy="11" fontSize="10.5">{_fmtInt(bitRow.pressurePsi)} psi</tspan>
                      <tspan x={fcpX - 8} dy="11" fontSize="8.5" fontWeight="400" fill="currentColor" opacity="0.85">Kill mud reaches bit</tspan>
                    </text>
                  </>
                );
              })()}
              {shoeRow && (() => {
                const shoeX = x(shoeRow.stroke), shoeY = y(shoeRow.pressurePsi);
                return (
                  <>
                    <circle cx={shoeX} cy={shoeY} r="4" fill="#f59e0b"
                            data-testid={`${testIdPrefix}-shoe-point`} />
                    <text x={shoeX} y={Math.min(H - PAD_B - 6, shoeY + 16)} textAnchor="middle"
                          fontSize="8.5" fontWeight="700" fill="#f59e0b"
                          data-testid={`${testIdPrefix}-shoe-label`}>
                      Kill mud reaches shoe
                    </text>
                  </>
                );
              })()}
              {surfaceRow && (() => {
                const surfX = x(surfaceRow.stroke), surfY = y(surfaceRow.pressurePsi);
                return (
                  <>
                    <circle cx={surfX} cy={surfY} r="4" fill="#f59e0b"
                            data-testid={`${testIdPrefix}-surface-point`} />
                    <text x={surfX - 4} y={Math.min(H - PAD_B - 6, surfY + 16)} textAnchor="end"
                          fontSize="8.5" fontWeight="700" fill="#f59e0b"
                          data-testid={`${testIdPrefix}-surface-label`}>
                      Kill mud reaches surface
                    </text>
                  </>
                );
              })()}
            </g>
          );
        })()}
      </svg>

      {/* Legend — wording corrected (ticket 237121, item 4; extended for
          the P0 Surface→Bit→Shoe→Surface display correction) to match what
          the schedule generator actually computes. There is no separate
          "circulate ramp" (Phase 1 is one anchor point, not a multi-point
          segment). */}
      <div className="flex flex-wrap items-center gap-4 mt-1 text-[10.5px] opacity-80">
        {circulate.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "#38bdf8", display: "inline-block" }} />
            Hold ICP · circulate kick out
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 14, height: 2, background: "#f59e0b", display: "inline-block" }} />
          Kill-mud displacement — drill string (Initial Circulating Pressure → Final Circulating Pressure)
        </span>
        {(bitToShoe.length > 0 || shoeToSurface.length > 0) && (
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 14, height: 2, background: "#f59e0b", display: "inline-block" }} />
            FCP held — bit → shoe → surface
          </span>
        )}
        {showMaasp && (
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 14, height: 0, borderTop: "1.5px dashed #dc2626", display: "inline-block" }} />
            MAASP reference
          </span>
        )}
      </div>
    </div>
  );
}

export default ScheduleChart;
